import React from "react";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { listJobs } from "../../api/jobs";
import { listCrmContacts } from "../../api/crm";
import {
  createInvoice,
  downloadInvoiceAdjusterCsv,
  downloadInvoiceContractorPdf,
  getInvoice,
  getInvoiceJobDefaults,
  getInvoiceJobFieldOptions,
  getInvoiceLogoDefault,
  listInvoices,
  updateInvoice,
} from "../../api/invoices";

const PDF_WIDTH = 612;
const PDF_HEIGHT = 792;
const PREVIEW_SCALE = 0.5;

const PREVIEW_FIELDS = [
  "logo",
  "header",
  "job_name",
  "invoice_number",
  "claim_number",
  "carrier",
  "service_start_date",
  "service_end_date",
  "insured_name",
  "bill_to",
  "line_items",
  "totals",
  "notes",
];

const FIELD_LABELS = {
  logo: "Logo",
  header: "Header",
  job_name: "Job Name",
  invoice_number: "Invoice #",
  claim_number: "Claim #",
  carrier: "Carrier",
  service_start_date: "Service Start",
  service_end_date: "Service End",
  insured_name: "Insured",
  bill_to: "Bill To",
  line_items: "Line Items",
  totals: "Totals",
  notes: "Notes",
};

const DEFAULT_LAYOUT = {
  logo: { x: 420, y: 44, w: 140, h: 70, enabled: 1 },
  header: { x: 48, y: 44, w: 330, h: 48, enabled: 1 },
  job_name: { x: 48, y: 96, w: 260, h: 32, enabled: 1 },
  invoice_number: { x: 48, y: 132, w: 180, h: 30, enabled: 1 },
  claim_number: { x: 232, y: 132, w: 180, h: 30, enabled: 1 },
  carrier: { x: 416, y: 132, w: 152, h: 30, enabled: 1 },
  service_start_date: { x: 48, y: 166, w: 180, h: 30, enabled: 1 },
  service_end_date: { x: 232, y: 166, w: 180, h: 30, enabled: 1 },
  insured_name: { x: 416, y: 166, w: 152, h: 30, enabled: 1 },
  bill_to: { x: 48, y: 200, w: 520, h: 48, enabled: 1 },
  line_items: { x: 48, y: 256, w: 520, h: 300, enabled: 1 },
  totals: { x: 360, y: 566, w: 208, h: 84, enabled: 1 },
  notes: { x: 48, y: 656, w: 520, h: 120, enabled: 1 },
};

const LIMITS = {
  logo: { minW: 40, minH: 20, maxW: 260, maxH: 180 },
  header: { minW: 200, minH: 34, maxW: 560, maxH: 120 },
  job_name: { minW: 160, minH: 24, maxW: 560, maxH: 80 },
  invoice_number: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  claim_number: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  carrier: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  service_start_date: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  service_end_date: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  insured_name: { minW: 130, minH: 24, maxW: 560, maxH: 80 },
  bill_to: { minW: 220, minH: 34, maxW: 560, maxH: 180 },
  line_items: { minW: 320, minH: 120, maxW: 560, maxH: 430 },
  totals: { minW: 160, minH: 56, maxW: 320, maxH: 200 },
  notes: { minW: 220, minH: 56, maxW: 560, maxH: 260 },
};

const DEFAULT_LINE_ITEM_CONFIG = [
  { key: "room", label: "Room", selected: true },
  { key: "category", label: "Category", selected: true },
  { key: "description", label: "Description", selected: true },
  { key: "quantity", label: "Qty", selected: true },
  { key: "unit", label: "Unit", selected: true },
  { key: "unit_price", label: "Unit Price", selected: true },
  { key: "line_total", label: "Line Total", selected: true },
];

function blankItem() {
  return { room: "", category: "", description: "", quantity: "", unit: "", unit_price: "", is_credit: false };
}

function normalizeLayout(input) {
  const src = input || DEFAULT_LAYOUT;
  const out = {};
  for (const key of PREVIEW_FIELDS) {
    const def = DEFAULT_LAYOUT[key];
    out[key] = {
      x: Number(src?.[key]?.x ?? def.x),
      y: Number(src?.[key]?.y ?? def.y),
      w: Number(src?.[key]?.w ?? def.w),
      h: Number(src?.[key]?.h ?? def.h),
      enabled: Number(src?.[key]?.enabled ?? def.enabled ?? 1) ? 1 : 0,
    };
  }
  return out;
}

function normalizeLineItemConfig(input) {
  const byKey = Object.fromEntries(DEFAULT_LINE_ITEM_CONFIG.map((row) => [row.key, { ...row }]));
  for (const raw of Array.isArray(input) ? input : []) {
    const key = String(raw?.key || "").trim();
    if (!byKey[key]) continue;
    byKey[key] = {
      key,
      label: String(raw?.label || byKey[key].label).trim().slice(0, 80) || byKey[key].label,
      selected: key === "description" ? true : Boolean(raw?.selected),
    };
  }
  return DEFAULT_LINE_ITEM_CONFIG.map((row) => ({ ...byKey[row.key] }));
}

function clamp(n, low, high) {
  return Math.max(low, Math.min(high, n));
}

function parseNumber(value, fallback = 0) {
  const n = Number(String(value ?? "").trim());
  return Number.isFinite(n) ? n : fallback;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image."));
    reader.readAsDataURL(file);
  });
}

function formatBillToFromContact(contact) {
  return [contact?.name, contact?.company, contact?.email, contact?.phone].map((x) => String(x || "").trim()).filter(Boolean).join(" | ");
}

function emptyFieldOptions() {
  return {
    header_text: [], invoice_number: [], insured_name: [], claim_number: [], carrier: [],
    bill_to: [], issued_date: [], service_start_date: [], service_end_date: [], tax_rate: [], notes: [],
    room: [], category: [], description: [], quantity: [], unit: [], unit_price: [],
  };
}

function addOption(list, value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return Array.isArray(list) ? list : [];
  const existing = Array.isArray(list) ? list : [];
  return [trimmed, ...existing.filter((item) => String(item || "").trim() !== trimmed)].slice(0, 80);
}

function mergeFieldOptions(current, additions) {
  const next = { ...emptyFieldOptions(), ...(current || {}) };
  for (const [key, values] of Object.entries(additions || {})) {
    for (const value of Array.isArray(values) ? values : [values]) {
      next[key] = addOption(next[key], value);
    }
  }
  return next;
}

function fieldOptionsFromInvoiceForm(form, items) {
  const options = {
    header_text: form.header_text,
    invoice_number: form.invoice_number,
    insured_name: form.insured_name,
    claim_number: form.claim_number,
    carrier: form.carrier,
    bill_to: form.bill_to,
    issued_date: form.issued_date,
    service_start_date: form.service_start_date,
    service_end_date: form.service_end_date,
    tax_rate: form.tax_rate,
    notes: form.notes,
    room: [],
    category: [],
    description: [],
    quantity: [],
    unit: [],
    unit_price: [],
  };
  for (const item of Array.isArray(items) ? items : []) {
    options.room.push(item.room);
    options.category.push(item.category);
    options.description.push(item.description);
    options.quantity.push(item.quantity);
    options.unit.push(item.unit);
    options.unit_price.push(item.unit_price);
  }
  return options;
}

export default function InvoiceStudio() {
  const [jobs, setJobs] = React.useState([]);
  const [jobId, setJobId] = React.useState("");
  const [selectedJobIds, setSelectedJobIds] = React.useState([]);
  const [jobNameInput, setJobNameInput] = React.useState("");
  const [invoices, setInvoices] = React.useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [busyPdf, setBusyPdf] = React.useState(false);
  const [busyCsv, setBusyCsv] = React.useState(false);
  const [error, setError] = React.useState("");
  const [crmContacts, setCrmContacts] = React.useState([]);
  const [billToContactId, setBillToContactId] = React.useState("");
  const [logoDataUrl, setLogoDataUrl] = React.useState("");
  const [layout, setLayout] = React.useState(() => normalizeLayout(DEFAULT_LAYOUT));
  const [lineItemConfig, setLineItemConfig] = React.useState(() => normalizeLineItemConfig(DEFAULT_LINE_ITEM_CONFIG));
  const [dragging, setDragging] = React.useState(null);
  const previewRef = React.useRef(null);
  const [form, setForm] = React.useState({
    invoice_number: "", header_text: "", insured_name: "", claim_number: "", carrier: "", bill_to: "",
    issued_date: "", service_start_date: "", service_end_date: "", tax_rate: "0", notes: "",
  });
  const [items, setItems] = React.useState([blankItem()]);
  const [fieldOptions, setFieldOptions] = React.useState(() => emptyFieldOptions());
  const templateAppliedRef = React.useRef("");
  const visibleColumns = React.useMemo(() => {
    const cols = lineItemConfig.filter((c) => c.selected || c.key === "description").map((c) => c.key);
    if (!cols.includes("description")) cols.unshift("description");
    return cols;
  }, [lineItemConfig]);
  const showField = React.useCallback((key) => visibleColumns.includes(key), [visibleColumns]);
  const lineItemLabel = React.useCallback((key, fallback) => {
    const row = lineItemConfig.find((c) => c.key === key);
    const label = String(row?.label || fallback || "").trim();
    return label || fallback;
  }, [lineItemConfig]);

  const refreshJobs = React.useCallback(async () => {
    const rows = await listJobs({ limit: 300, offset: 0 });
    const list = Array.isArray(rows) ? rows : [];
    setJobs(list);
  }, []);

  const refreshInvoices = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await listInvoices({ jobId: jobId || "", limit: 400, offset: 0 });
      setInvoices(Array.isArray(res?.items) ? res.items : []);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  React.useEffect(() => { refreshJobs().catch(() => {}); }, [refreshJobs]);
  React.useEffect(() => { refreshInvoices().catch(() => {}); }, [refreshInvoices]);

  React.useEffect(() => {
    let active = true;
    getInvoiceLogoDefault()
      .then((data) => {
        if (!active) return;
        const logo = String(data?.logo_data_url || "").trim();
        if (logo) setLogoDataUrl((prev) => String(prev || "").trim() || logo);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    const name = String(jobNameInput || "").trim().toLowerCase();
    if (!name) {
      setJobId("");
      return;
    }
    const match = jobs.find((j) => String(j?.name || "").trim().toLowerCase() === name);
    setJobId(match?.id ? String(match.id) : "");
  }, [jobNameInput, jobs]);

  React.useEffect(() => {
    let active = true;
    if (!jobId) {
      setCrmContacts([]);
      return () => { active = false; };
    }
    listCrmContacts(jobId).then((rows) => {
      if (!active) return;
      setCrmContacts(Array.isArray(rows) ? rows : []);
    }).catch(() => { if (active) setCrmContacts([]); });
    return () => { active = false; };
  }, [jobId, jobNameInput]);

  React.useEffect(() => {
    let active = true;
    getInvoiceJobFieldOptions(jobId).then((res) => {
      if (active) setFieldOptions({ ...emptyFieldOptions(), ...(res?.fields || {}) });
    }).catch(() => { if (active) setFieldOptions(emptyFieldOptions()); });
    return () => { active = false; };
  }, [jobId, jobNameInput]);

  React.useEffect(() => {
    let active = true;
    if (!jobId || selectedInvoiceId) return () => { active = false; };
    if (templateAppliedRef.current === String(jobId)) return () => { active = false; };
    getInvoiceJobDefaults(jobId).then((data) => {
      if (!active) return;
      setForm((prev) => ({
        ...prev,
        invoice_number: prev.invoice_number || String(data?.invoice_number || ""),
        header_text: prev.header_text || String(data?.header_text || ""),
        insured_name: prev.insured_name || String(data?.insured_name || ""),
        claim_number: prev.claim_number || String(data?.claim_number || ""),
        carrier: prev.carrier || String(data?.carrier || ""),
        bill_to: prev.bill_to || String(data?.bill_to || ""),
        issued_date: prev.issued_date || String(data?.issued_date || ""),
        service_start_date: prev.service_start_date || String(data?.service_start_date || ""),
        service_end_date: prev.service_end_date || String(data?.service_end_date || ""),
        tax_rate: prev.tax_rate || String(data?.tax_rate ?? "0"),
        notes: prev.notes || String(data?.notes || ""),
      }));
      if (!String(logoDataUrl || "").trim() && String(data?.logo_data_url || "").trim()) {
        setLogoDataUrl(String(data.logo_data_url));
      }
      if (data?.layout) setLayout(normalizeLayout(data.layout));
      if (data?.line_item_config) setLineItemConfig(normalizeLineItemConfig(data.line_item_config));
      templateAppliedRef.current = String(jobId);
    }).catch(() => {});
    return () => { active = false; };
  }, [jobId, selectedInvoiceId, logoDataUrl]);

  React.useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (event) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect) return;
      setLayout((prev) => {
        const next = normalizeLayout(prev);
        const key = dragging.key;
        const block = next[key];
        if (!block) return next;
        if (dragging.mode === "resize") {
          const dx = (event.clientX - dragging.startClientX) / PREVIEW_SCALE;
          const dy = (event.clientY - dragging.startClientY) / PREVIEW_SCALE;
          const lim = LIMITS[key] || { minW: 80, minH: 24, maxW: PDF_WIDTH, maxH: PDF_HEIGHT };
          block.w = clamp(dragging.startW + dx, lim.minW, lim.maxW);
          block.h = clamp(dragging.startH + dy, lim.minH, lim.maxH);
          block.x = clamp(block.x, 0, PDF_WIDTH - block.w);
          block.y = clamp(block.y, 0, PDF_HEIGHT - block.h);
        } else {
          block.x = clamp((event.clientX - rect.left) / PREVIEW_SCALE - dragging.offsetX, 0, PDF_WIDTH - block.w);
          block.y = clamp((event.clientY - rect.top) / PREVIEW_SCALE - dragging.offsetY, 0, PDF_HEIGHT - block.h);
        }
        return next;
      });
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  const invoiceJobIds = React.useCallback(() => {
    const ids = [...selectedJobIds.map((id) => String(id || "").trim()), String(jobId || "").trim()].filter(Boolean);
    return Array.from(new Set(ids));
  }, [jobId, selectedJobIds]);

  const toggleInvoiceJob = (id, checked) => {
    const key = String(id || "").trim();
    if (!key) return;
    setSelectedJobIds((prev) => {
      const next = new Set(prev.map((x) => String(x || "").trim()).filter(Boolean));
      if (checked) next.add(key);
      else next.delete(key);
      return Array.from(next);
    });
  };

  const loadInvoice = async (id) => {
    const res = await getInvoice(String(id || "").trim());
    const invoice = res?.invoice || {};
    const invJobId = String(invoice.job_id || "");
    const linkedJobIds = Array.isArray(invoice.job_ids) ? invoice.job_ids.map((id) => String(id || "").trim()).filter(Boolean) : [];
    const job = jobs.find((j) => String(j?.id || "") === (linkedJobIds[0] || invJobId));
    setJobId(invJobId);
    setJobNameInput(String(job?.name || ""));
    setSelectedJobIds(linkedJobIds);
    setSelectedInvoiceId(String(invoice.id || ""));
    setForm({
      invoice_number: String(invoice.invoice_number || ""),
      header_text: String(invoice.header_text || ""),
      insured_name: String(invoice.insured_name || ""),
      claim_number: String(invoice.claim_number || ""),
      carrier: String(invoice.carrier || ""),
      bill_to: String(invoice.bill_to || ""),
      issued_date: String(invoice.issued_date || ""),
      service_start_date: String(invoice.service_start_date || ""),
      service_end_date: String(invoice.service_end_date || ""),
      tax_rate: String(invoice.tax_rate ?? "0"),
      notes: String(invoice.notes || ""),
    });
    setLogoDataUrl(String(invoice.logo_data_url || ""));
    setLayout(normalizeLayout(invoice.layout));
    setLineItemConfig(normalizeLineItemConfig(invoice.line_item_config));
    setItems(Array.isArray(invoice.items) && invoice.items.length ? invoice.items.map((r) => ({
      room: String(r.room || ""), category: String(r.category || ""), description: String(r.description || ""),
      quantity: String(r.quantity ?? ""), unit: String(r.unit || ""), unit_price: String(r.unit_price ?? ""),
      is_credit: Boolean(r.is_credit),
    })) : [blankItem()]);
  };

  const saveInvoice = async () => {
    setSaving(true);
    setError("");
    try {
      const linkedJobIds = invoiceJobIds();
      const payload = {
        job_id: linkedJobIds[0] || "",
        job_ids: linkedJobIds,
        invoice_number: String(form.invoice_number || "").trim(),
        header_text: String(form.header_text || "").trim() || null,
        insured_name: String(form.insured_name || "").trim() || null,
        claim_number: String(form.claim_number || "").trim() || null,
        carrier: String(form.carrier || "").trim() || null,
        bill_to: String(form.bill_to || "").trim() || null,
        issued_date: String(form.issued_date || "").trim() || null,
        service_start_date: String(form.service_start_date || "").trim() || null,
        service_end_date: String(form.service_end_date || "").trim() || null,
        tax_rate: parseNumber(form.tax_rate, 0),
        credit_amount: 0,
        notes: String(form.notes || "").trim() || null,
        logo_data_url: String(logoDataUrl || "").trim() || null,
        layout_json: JSON.stringify(normalizeLayout(layout)),
        line_item_config_json: JSON.stringify(normalizeLineItemConfig(lineItemConfig)),
        items: items.map((it) => ({
          room: String(it.room || "").trim() || null,
          category: String(it.category || "").trim() || null,
          description: String(it.description || "").trim(),
          quantity: parseNumber(it.quantity, 0),
          unit: String(it.unit || "EA").trim() || "EA",
          unit_price: parseNumber(it.unit_price, 0),
          is_credit: Boolean(it.is_credit),
        })).filter((it) => it.description),
      };
      if (!payload.invoice_number) throw new Error("Invoice number is required.");
      const saved = selectedInvoiceId ? await updateInvoice(selectedInvoiceId, payload) : await createInvoice(payload);
      const id = String(saved?.invoice?.id || "").trim();
      if (id) setSelectedInvoiceId(id);
      setFieldOptions((prev) => mergeFieldOptions(prev, fieldOptionsFromInvoiceForm(form, items)));
      await refreshInvoices();
      if (linkedJobIds[0]) {
        try {
          const options = await getInvoiceJobFieldOptions(linkedJobIds[0]);
          setFieldOptions((prev) => mergeFieldOptions(prev, options?.fields || {}));
        } catch (_e) {
          // The optimistic merge above keeps current inputs selectable even if the refresh fails.
        }
      }
      return id;
    } catch (e) {
      setError(e?.message || "Failed to save invoice.");
      return "";
    } finally {
      setSaving(false);
    }
  };

  const saveInvoiceBeforeExport = React.useCallback(async () => {
    const id = await saveInvoice();
    if (!id) throw new Error("Failed to save invoice.");
    return id;
  }, [saveInvoice]);

  const subtotal = items.reduce(
    (sum, it) => sum + (it.is_credit ? 0 : parseNumber(it.quantity) * parseNumber(it.unit_price)),
    0
  );
  const tax = subtotal * parseNumber(form.tax_rate, 0);
  const credit = items.reduce(
    (sum, it) => sum + (it.is_credit ? parseNumber(it.quantity) * parseNumber(it.unit_price) : 0),
    0
  );
  const total = Math.max(0, subtotal + tax - credit);
  const previewWidth = PDF_WIDTH * PREVIEW_SCALE;
  const previewHeight = PDF_HEIGHT * PREVIEW_SCALE;

  const dragHandle = (event, key, offsetX, offsetY) => {
    event.preventDefault();
    setDragging({ mode: "move", key, offsetX, offsetY });
  };

  const resizeBlockHandle = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    const block = layout?.[key];
    if (!block) return;
    setDragging({ mode: "resize", key, startClientX: event.clientX, startClientY: event.clientY, startW: Number(block.w || 80), startH: Number(block.h || 24) });
  };
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-2xl font-semibold text-slate-900">Invoice Studio</h1>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm text-slate-700 sm:col-span-2">
              Job filter / quick link
              <Input className="mt-1" list="invoice-job-options" value={jobNameInput} maxLength={200} onChange={(e) => { setJobNameInput(e.target.value); setSelectedInvoiceId(""); }} placeholder="Optional: type or select a job" />
            </label>
            <label className="text-sm text-slate-700"><span>Invoice #</span><Input className="mt-1" list="invoice-no-options" maxLength={160} value={form.invoice_number} onChange={(e) => setForm((p) => ({ ...p, invoice_number: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Header</span><Input className="mt-1" list="invoice-header-options" maxLength={400} value={form.header_text} onChange={(e) => setForm((p) => ({ ...p, header_text: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Issued Date</span><Input className="mt-1" type="date" list="invoice-issued-options" value={form.issued_date} onChange={(e) => setForm((p) => ({ ...p, issued_date: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Insured</span><Input className="mt-1" list="invoice-insured-options" maxLength={300} value={form.insured_name} onChange={(e) => setForm((p) => ({ ...p, insured_name: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Claim #</span><Input className="mt-1" list="invoice-claim-options" maxLength={200} value={form.claim_number} onChange={(e) => setForm((p) => ({ ...p, claim_number: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Carrier</span><Input className="mt-1" list="invoice-carrier-options" maxLength={300} value={form.carrier} onChange={(e) => setForm((p) => ({ ...p, carrier: e.target.value }))} /></label>
            <label className="text-sm text-slate-700">
              Bill To (CRM)
              <select className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800" value={billToContactId} onChange={(e) => {
                const id = String(e.target.value || "").trim();
                setBillToContactId(id);
                if (!id) return;
                const contact = crmContacts.find((c) => String(c?.id || "") === id);
                if (contact) setForm((p) => ({ ...p, bill_to: formatBillToFromContact(contact) }));
              }}>
                <option value="">Select contact</option>
                {crmContacts.map((c) => <option key={String(c?.id || "")} value={String(c?.id || "")}>{String(c?.name || "Unnamed")}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-700"><span>Bill To</span><Input className="mt-1" list="invoice-billto-options" maxLength={4000} value={form.bill_to} onChange={(e) => setForm((p) => ({ ...p, bill_to: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Service Start</span><Input className="mt-1" type="date" list="invoice-service-start-options" value={form.service_start_date} onChange={(e) => setForm((p) => ({ ...p, service_start_date: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Service End</span><Input className="mt-1" type="date" list="invoice-service-end-options" value={form.service_end_date} onChange={(e) => setForm((p) => ({ ...p, service_end_date: e.target.value }))} /></label>
            <label className="text-sm text-slate-700"><span>Tax Rate (0-1)</span><Input className="mt-1" list="invoice-tax-rate-options" maxLength={20} value={form.tax_rate} onChange={(e) => setForm((p) => ({ ...p, tax_rate: e.target.value }))} /></label>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Linked Jobs</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedJobIds([]);
                  setJobId("");
                  setJobNameInput("");
                }}
              >
                Clear linked jobs
              </Button>
            </div>
            <div className="mt-2 max-h-44 overflow-auto rounded border border-slate-100">
              {jobs.length ? jobs.map((job) => {
                const id = String(job?.id || "");
                const checked = invoiceJobIds().includes(id);
                return (
                  <label key={`invoice-job-link-${id}`} className="flex items-center gap-2 border-t border-slate-100 px-3 py-2 text-sm first:border-t-0">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleInvoiceJob(id, e.target.checked)}
                    />
                    <span className="text-slate-800">{String(job?.name || "").trim() || "(untitled)"}</span>
                  </label>
                );
              }) : <p className="px-3 py-2 text-sm text-slate-500">No jobs available to link.</p>}
            </div>
          </div>
          <label className="block text-sm text-slate-700">Notes<textarea className="mt-1 min-h-[96px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" maxLength={20000} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></label>
          {(fieldOptions.notes || []).length ? (
            <label className="block text-sm text-slate-700">
              Saved Notes
              <select className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800" defaultValue="" onChange={(e) => {
                const value = String(e.target.value || "");
                if (value) setForm((p) => ({ ...p, notes: value }));
              }}>
                <option value="">Select saved notes</option>
                {(fieldOptions.notes || []).map((v) => <option key={`note-${v}`} value={v}>{v.length > 120 ? `${v.slice(0, 117)}...` : v}</option>)}
              </select>
            </label>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-700">Logo Upload
              <input className="mt-1 block w-full text-sm" type="file" accept="image/png,image/jpeg,image/webp" onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                try { setLogoDataUrl(await readFileAsDataUrl(file)); } catch (e) { setError(e?.message || "Failed to upload logo."); }
              }} />
            </label>
            <div className="flex items-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setLogoDataUrl("")}>Remove Logo</Button>
              <Button variant="ghost" size="sm" onClick={() => setLayout(normalizeLayout(DEFAULT_LAYOUT))}>Reset Layout</Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h2 className="text-lg font-semibold text-slate-900">Invoice Preview Layout</h2></CardHeader>
        <CardBody>
          <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {PREVIEW_FIELDS.map((key) => (
              <label key={key} className="inline-flex items-center gap-2 rounded border border-slate-200 px-2 py-1 text-xs text-slate-700">
                <input type="checkbox" checked={Boolean(layout?.[key]?.enabled ?? 1)} onChange={(e) => setLayout((prev) => ({ ...prev, [key]: { ...prev[key], enabled: e.target.checked ? 1 : 0 } }))} />
                {FIELD_LABELS[key]}
              </label>
            ))}
          </div>
          <div ref={previewRef} className="relative rounded-lg border border-slate-300 bg-white shadow-inner" style={{ width: `${previewWidth}px`, height: `${previewHeight}px` }}>
            {PREVIEW_FIELDS.filter((key) => Number(layout?.[key]?.enabled ?? 1)).map((key) => (
              <div key={key} className={`absolute cursor-move rounded border px-1 py-0.5 text-[10px] ${key === "logo" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-400 bg-slate-100 text-slate-700"}`} style={{ left: `${layout[key].x * PREVIEW_SCALE}px`, top: `${layout[key].y * PREVIEW_SCALE}px`, width: `${layout[key].w * PREVIEW_SCALE}px`, height: `${layout[key].h * PREVIEW_SCALE}px`, overflow: "hidden" }} onMouseDown={(e) => dragHandle(e, key, (e.clientX - previewRef.current.getBoundingClientRect().left) / PREVIEW_SCALE - layout[key].x, (e.clientY - previewRef.current.getBoundingClientRect().top) / PREVIEW_SCALE - layout[key].y)}>
                {key === "logo" && logoDataUrl ? <img src={logoDataUrl} alt="Invoice logo" className="h-full w-full object-contain" /> : <div className="break-words p-1">{FIELD_LABELS[key]}</div>}
                <button type="button" aria-label={`Resize ${key}`} className={`absolute bottom-0 right-0 h-3 w-3 translate-x-1/2 translate-y-1/2 rounded-sm border cursor-nwse-resize ${key === "logo" ? "border-indigo-700 bg-indigo-600" : "border-slate-700 bg-slate-600"}`} onMouseDown={(e) => resizeBlockHandle(e, key)} />
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader><h2 className="text-lg font-semibold text-slate-900">Line Items</h2></CardHeader>
        <CardBody className="space-y-3">
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Line Item Fields In Preview</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {lineItemConfig.map((cfg) => (
                <div key={`cfg-${cfg.key}`} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(cfg.selected || cfg.key === "description")}
                    disabled={cfg.key === "description"}
                    onChange={(e) => setLineItemConfig((prev) => prev.map((row) => (row.key === cfg.key ? { ...row, selected: e.target.checked } : row)))}
                  />
                  <Input
                    className="h-9"
                    maxLength={60}
                    value={cfg.label}
                    onChange={(e) => setLineItemConfig((prev) => prev.map((row) => (row.key === cfg.key ? { ...row, label: e.target.value } : row)))}
                  />
                </div>
              ))}
            </div>
          </div>
          {items.map((it, idx) => (
            <div key={`item-${idx}`} className="grid gap-3 rounded-lg border border-slate-200 p-3 md:grid-cols-12">
              {showField("room") ? <Input className="h-12 md:col-span-2" list="invoice-room-options" maxLength={300} placeholder={lineItemLabel("room", "Room")} value={it.room} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, room: e.target.value } : r)))} /> : null}
              {showField("category") ? <Input className="h-12 md:col-span-2" list="invoice-category-options" maxLength={300} placeholder={lineItemLabel("category", "Category")} value={it.category} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, category: e.target.value } : r)))} /> : null}
              <Input className="h-12 md:col-span-4" list="invoice-description-options" maxLength={4000} placeholder={lineItemLabel("description", "Description")} value={it.description} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, description: e.target.value } : r)))} />
              {showField("quantity") ? <Input className="h-12 md:col-span-1" list="invoice-quantity-options" maxLength={20} placeholder={lineItemLabel("quantity", "Qty")} value={it.quantity} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r)))} /> : null}
              {showField("unit") ? <Input className="h-12 md:col-span-1" list="invoice-unit-options" maxLength={120} placeholder={lineItemLabel("unit", "Unit")} value={it.unit} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, unit: e.target.value } : r)))} /> : null}
              {showField("unit_price") ? <Input className="h-12 md:col-span-2" list="invoice-unit-price-options" maxLength={20} placeholder={lineItemLabel("unit_price", "Unit Price")} value={it.unit_price} onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, unit_price: e.target.value } : r)))} /> : null}
              <div className="flex flex-wrap items-center gap-3 md:col-span-12">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(it.is_credit)}
                    onChange={(e) => setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, is_credit: e.target.checked } : r)))}
                  />
                  Credit
                </label>
                <span className={it.is_credit ? "text-sm font-semibold text-emerald-700" : "text-sm text-slate-500"}>
                  {it.is_credit ? `Applies -$${(parseNumber(it.quantity) * parseNumber(it.unit_price)).toFixed(2)}` : "Regular charge"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}>Remove Line Item</Button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setItems((prev) => [...prev, blankItem()])}>Add Line Item</Button>
            <Button onClick={saveInvoice} disabled={saving}>{saving ? "Saving..." : selectedInvoiceId ? "Update Invoice" : "Create Invoice"}</Button>
            <Button variant="secondary" onClick={async () => {
              setBusyPdf(true);
              try { await downloadInvoiceContractorPdf(await saveInvoiceBeforeExport()); }
              catch (e) { setError(e?.message || "Failed PDF export."); }
              finally { setBusyPdf(false); }
            }} disabled={busyPdf || saving}>{busyPdf ? "Exporting..." : "Contractor PDF"}</Button>
            <Button variant="secondary" onClick={async () => {
              setBusyCsv(true);
              try { await downloadInvoiceAdjusterCsv(await saveInvoiceBeforeExport()); }
              catch (e) { setError(e?.message || "Failed CSV export."); }
              finally { setBusyCsv(false); }
            }} disabled={busyCsv || saving}>{busyCsv ? "Exporting..." : "Adjuster CSV"}</Button>
          </div>
          <div className="text-sm text-slate-700">
            <p>Subtotal: ${subtotal.toFixed(2)}</p>
            <p>Tax: ${tax.toFixed(2)}</p>
            {credit > 0 ? <p>Credit: -${credit.toFixed(2)}</p> : null}
            <p className="font-semibold">Total: ${total.toFixed(2)}</p>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h2 className="text-lg font-semibold text-slate-900">Saved Invoices</h2></CardHeader>
        <CardBody>
          {loading ? <p className="text-sm text-slate-500">Loading...</p> : invoices.length ? (
            <ul className="space-y-2">
              {invoices.map((inv) => (
                <li key={String(inv?.id || "")} className="flex items-center justify-between rounded-lg border border-slate-200 p-2 text-sm">
                  <span>{String(inv?.invoice_number || "")} | {String(inv?.issued_date || "no-date")} | {String(inv?.claim_number || "")}</span>
                  <Button size="sm" variant="ghost" onClick={() => loadInvoice(inv?.id)}>Load</Button>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">No invoices found.</p>}
        </CardBody>
      </Card>

      <datalist id="invoice-job-options">{jobs.map((j) => <option key={`job-${String(j?.id || "")}`} value={String(j?.name || "")} />)}</datalist>
      <datalist id="invoice-header-options">{(fieldOptions.header_text || []).map((v) => <option key={`h-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-no-options">{(fieldOptions.invoice_number || []).map((v) => <option key={`in-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-insured-options">{(fieldOptions.insured_name || []).map((v) => <option key={`i-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-claim-options">{(fieldOptions.claim_number || []).map((v) => <option key={`c-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-carrier-options">{(fieldOptions.carrier || []).map((v) => <option key={`ca-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-billto-options">{(fieldOptions.bill_to || []).map((v) => <option key={`b-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-issued-options">{(fieldOptions.issued_date || []).map((v) => <option key={`iss-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-service-start-options">{(fieldOptions.service_start_date || []).map((v) => <option key={`ss-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-service-end-options">{(fieldOptions.service_end_date || []).map((v) => <option key={`se-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-tax-rate-options">{(fieldOptions.tax_rate || []).map((v) => <option key={`tax-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-room-options">{(fieldOptions.room || []).map((v) => <option key={`r-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-category-options">{(fieldOptions.category || []).map((v) => <option key={`cat-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-description-options">{(fieldOptions.description || []).map((v) => <option key={`d-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-quantity-options">{(fieldOptions.quantity || []).map((v) => <option key={`q-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-unit-options">{(fieldOptions.unit || []).map((v) => <option key={`u-${v}`} value={v} />)}</datalist>
      <datalist id="invoice-unit-price-options">{(fieldOptions.unit_price || []).map((v) => <option key={`up-${v}`} value={v} />)}</datalist>
    </div>
  );
}
